/**
 * Unsent "Prompt a draft…" text, keyed per thread / conversation / script.
 *
 * In-memory so Close thread + same-session remounts restore instantly.
 * localStorage so the text also survives leaving Home for Projects, a
 * refresh, or a mobile WebView recycle. Entries expire after a week so
 * stale prompts do not linger.
 *
 * Caps stored entries so a long browsing session cannot grow unbounded.
 */
const STORAGE_KEY = 'lh-prompt-drafts:v1'
const MAX_ENTRIES = 50
const TTL_MS = 7 * 24 * 60 * 60 * 1000

type StoredDraft = { text: string; updatedAt: number }
type DraftStore = Record<string, StoredDraft>

const memory = new Map<string, string>()

function storage(): Storage | null {
    try {
        if (typeof localStorage === 'undefined') return null
        return localStorage
    } catch {
        return null
    }
}

function isFresh(entry: StoredDraft, now = Date.now()) {
    return now - (entry.updatedAt || 0) < TTL_MS
}

function readStore(): DraftStore {
    const backend = storage()
    if (!backend) return {}
    try {
        const raw = backend.getItem(STORAGE_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
        const store: DraftStore = {}
        for (const [key, value] of Object.entries(parsed)) {
            if (
                value &&
                typeof value === 'object' &&
                typeof (value as StoredDraft).text === 'string'
            ) {
                store[key] = {
                    text: (value as StoredDraft).text,
                    updatedAt: Number((value as StoredDraft).updatedAt) || 0,
                }
            }
        }
        return store
    } catch {
        return {}
    }
}

function writeStore(store: DraftStore) {
    const backend = storage()
    if (!backend) return
    try {
        backend.setItem(STORAGE_KEY, JSON.stringify(store))
    } catch {
        // Quota / private mode — memory cache still works for this session.
    }
}

function prune(store: DraftStore, now = Date.now()) {
    for (const key of Object.keys(store)) {
        if (!isFresh(store[key], now)) {
            delete store[key]
            memory.delete(key)
        }
    }
    const keys = Object.keys(store)
    if (keys.length <= MAX_ENTRIES) return
    keys
        .sort((a, b) => (store[a].updatedAt || 0) - (store[b].updatedAt || 0))
        .slice(0, keys.length - MAX_ENTRIES)
        .forEach((key) => {
            delete store[key]
            memory.delete(key)
        })
}

export function threadPromptDraftKey(threadId: string | null | undefined): string | null {
    return threadId ? `thread:${threadId}` : null
}

export function conversationPromptDraftKey(
    conversationId: string | null | undefined,
): string | null {
    return conversationId ? `conversation:${conversationId}` : null
}

export function scriptPromptDraftKey(documentId: string | null | undefined): string | null {
    return documentId ? `script:${documentId}` : null
}

export function readPromptDraft(key: string | null | undefined): string {
    if (!key) return ''
    if (memory.has(key)) return memory.get(key) ?? ''
    const entry = readStore()[key]
    if (!entry || !isFresh(entry)) {
        if (entry) {
            const store = readStore()
            delete store[key]
            writeStore(store)
        }
        return ''
    }
    memory.set(key, entry.text)
    return entry.text
}

export function writePromptDraft(key: string | null | undefined, value: string) {
    if (!key) return
    if (!value.trim()) {
        memory.delete(key)
        const store = readStore()
        if (store[key]) {
            delete store[key]
            writeStore(store)
        }
        return
    }
    memory.set(key, value)
    const store = readStore()
    store[key] = { text: value, updatedAt: Date.now() }
    prune(store)
    writeStore(store)
}

/** Test-only: drop in-memory drafts. localStorage is left to the caller. */
export function _resetPromptDraftMemory() {
    memory.clear()
}
